import { Eye, Pencil, Trash2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TagValueBadge } from './tag-value-badge'
import type { ResourceTagListItem } from '@/types/resource-tag'

interface ResourceTagsTableProps {
  items: ResourceTagListItem[]
  isLoading?: boolean
  onView: (tag: ResourceTagListItem) => void
  onEdit: (tag: ResourceTagListItem) => void
  onDelete: (tag: ResourceTagListItem) => void
}

function LoadingSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-40" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-48" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-8" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-8 w-24" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export function ResourceTagsTable({
  items,
  isLoading = false,
  onView,
  onEdit,
  onDelete,
}: ResourceTagsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data Classification</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-24">Records</TableHead>
            <TableHead className="w-36 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            items.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell className="font-mono text-sm font-medium">{tag.resourceTag}</TableCell>
                <TableCell>
                  <TagValueBadge value={tag.tagValue} />
                </TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">
                  {tag.description || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">{tag.recordCount}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onView(tag)}
                      aria-label={`View ${tag.resourceTag}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEdit(tag)}
                      aria-label={`Edit ${tag.resourceTag}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => onDelete(tag)}
                      aria-label={`Delete ${tag.resourceTag}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
